import React, { useEffect, useMemo, useRef, useState } from "react";
import { Node, OnCheckNode } from "react-checkbox-tree";
import { skipToken } from "@reduxjs/toolkit/query";

import { CheckboxTree } from "ui";

import { useGetVersionConfigQuery } from "fond/api";
import { LayerStyle, SublayerConfig } from "fond/types/ProjectLayerConfig";
import { BlockSpinner } from "fond/widgets";

import { generateLayerStyleTreeItems } from "./helper";

import { Container } from "./LayerStyleTree.styles";

interface LayerStyleTreeProps {
  versionId: string | undefined;
  excludedKeys?: string[];
  allowSublayerSelection?: boolean;
  onSelectionChange: (selectedStyles: LayerStyle[], selectedSublayers: SublayerConfig[]) => void;
}

const LayerStyleTree: React.FC<LayerStyleTreeProps> = ({
  versionId,
  excludedKeys,
  allowSublayerSelection = true,
  onSelectionChange,
}: LayerStyleTreeProps) => {
  const { data: config, isLoading, isFetching } = useGetVersionConfigQuery(versionId ?? skipToken, { skip: !versionId });
  const [checked, setChecked] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const treeRef = useRef<CheckboxTree>(null);

  const treeItems = useMemo(() => {
    return config ? generateLayerStyleTreeItems({ config, excludedKeys: excludedKeys || [], allowSublayerSelection }) : [];
  }, [config, excludedKeys, allowSublayerSelection]);

  // This effect is used to expand all nodes when the config changes
  useEffect(() => {
    treeRef.current?.onExpandAll();
  }, [config, excludedKeys]);

  // Check (resp. uncheck) the descendants of a checked (resp. unchecked) node
  const handleOnCheck = (checkedItems: string[], onCheckNode: OnCheckNode) => {
    if (!config) return;

    const clickedEntity = config.Data.entities[onCheckNode.value];

    // If trying to uncheck a style, check if its parent sublayer is selected
    if (!onCheckNode.checked && clickedEntity?.Type === "STYLE") {
      const style = clickedEntity;
      // If the style's parent sublayer is checked, prevent unchecking the style
      if (checkedItems.includes(style.ConfigurationID)) {
        // Don't allow unchecking - keep the current checked state
        return;
      }
    }

    const descendants: string[] = [];
    const traverse = (node: Node) => {
      descendants.push(node.value);
      node.children?.forEach((child) => {
        traverse(child);
      });
    };
    traverse(onCheckNode);

    const newChecked = onCheckNode.checked
      ? [...new Set([...checkedItems, ...descendants])]
      : checkedItems.filter((value) => !descendants.includes(value));

    setChecked(newChecked);

    // Separate styles and sublayers from the selection
    const selectedEntities = newChecked.map((id) => config.Data.entities[id]).filter((entity) => entity !== undefined);
    const selectedSublayers = selectedEntities.filter((entity): entity is SublayerConfig => entity?.Type === "SUBLAYER");

    // Create a set of selected sublayer IDs for quick lookup
    const selectedSublayerIds = new Set(selectedSublayers.map((sl) => sl.ID));

    // Only include styles that are NOT part of a selected sublayer
    // When a sublayer is selected, all its styles are included in the sublayer itself
    const selectedStyles = selectedEntities.filter((entity): entity is LayerStyle => {
      if (entity?.Type !== "STYLE") return false;
      const style = entity;
      // Exclude styles that belong to a selected sublayer
      return !selectedSublayerIds.has(style.ConfigurationID);
    });

    onSelectionChange(selectedStyles, selectedSublayers);
  };

  if (isLoading || isFetching) return <BlockSpinner containerProps={{ height: "100%" }} />;

  return (
    <Container data-test-id="layer-style-tree">
      <CheckboxTree ref={treeRef} checked={checked} expanded={expanded} onCheck={handleOnCheck} onExpand={setExpanded} nodes={treeItems} noCascade />
    </Container>
  );
};

export default LayerStyleTree;
